import { genders } from 'constants/campaign';
import { gigTypes } from 'constants/global';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { fetchCampaignCreateDetails } from 'slices/campaigns/details/thunk';
import { AppDispatch, RootState } from 'store';
import { CampaignIdea } from 'types/campaign';
import { GigType, Milestone } from 'types/campaign/collaborations';
import Skeleton from 'react-loading-skeleton';

interface AICampaignIdeasProps {
    campaignIdeas: CampaignIdea[];
    onIdeaSelect: (idea: CampaignIdea) => void
    loading: boolean,
    generating?: boolean
}

function useStreamingTypewriterText(
    text: string,
    {
        enabled = true,
        charsPerSecond = 60,
    }: { enabled?: boolean; charsPerSecond?: number } = {}
) {
    const [displayed, setDisplayed] = useState<string>(enabled ? '' : text);
    const lastTargetRef = useRef<string>(text);
    const rafRef = useRef<number | null>(null);
    const lastTsRef = useRef<number | null>(null);
    const carryRef = useRef<number>(0);

    useEffect(() => {
        if (!enabled) {
            setDisplayed(text);
            lastTargetRef.current = text;
            return;
        }

        const prevTarget = lastTargetRef.current;
        lastTargetRef.current = text;

        setDisplayed((prev) => {
            if (!text) return '';
            if (!prev) return '';

            // Normal streaming append: keep what we already typed.
            if (text.startsWith(prev)) return prev;

            // New content (e.g. selecting a different idea): restart typing.
            if (prevTarget && text !== prevTarget) return '';

            // If target shrinks, restart.
            if (text.length < prev.length) return '';

            return prev;
        });
    }, [text, enabled]);

    useEffect(() => {
        if (!enabled) return;
        if (!text) {
            setDisplayed('');
            return;
        }

        const step = (ts: number) => {
            if (lastTsRef.current == null) lastTsRef.current = ts;
            const deltaMs = ts - lastTsRef.current;
            lastTsRef.current = ts;

            const charsToAdd = (charsPerSecond * deltaMs) / 1000 + carryRef.current;
            const whole = Math.floor(charsToAdd);
            carryRef.current = charsToAdd - whole;

            if (whole > 0) {
                setDisplayed((prev) => {
                    if (prev.length >= text.length) return prev;
                    const nextLen = Math.min(text.length, prev.length + whole);
                    return text.slice(0, nextLen);
                });
            }

            rafRef.current = requestAnimationFrame(step);
        };

        rafRef.current = requestAnimationFrame(step);

        return () => {
            if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
            rafRef.current = null;
            lastTsRef.current = null;
            carryRef.current = 0;
        };
    }, [text, enabled, charsPerSecond]);

    const isComplete = displayed.length >= text.length;
    return { displayed, isComplete };
}

const TypewriterText: React.FC<{
    text: string;
    enabled?: boolean;
    charsPerSecond?: number;
    className?: string;
    showCaret?: boolean;
}> = ({ text, enabled = true, charsPerSecond = 60, className, showCaret = true }) => {
    const { displayed, isComplete } = useStreamingTypewriterText(text, { enabled, charsPerSecond });

    return (
        <span className={className}>
            {displayed}
            {showCaret && enabled && !isComplete ? <span aria-hidden className="campaign-ai-typewriter__caret" /> : null}
        </span>
    );
};
const AICampaignIdeas: React.FC<AICampaignIdeasProps> = ({ campaignIdeas, onIdeaSelect, loading, generating = false }) => {


    const [selectedIdeaUUID, setSelectedIdeaUUID] = useState<string | null>(null);

    const dispatch: AppDispatch = useDispatch();


    const campaignDetails = useSelector((state: RootState) => state.campaignDetails.campaigns['create'])
    const goalList = campaignDetails?.goal_list ?? {};
    const gigTypesArr = campaignDetails?.gig_types ?? [];

    const gigTypes = useMemo(() => {
        return gigTypesArr.reduce((acc, gigType) => {
            acc[gigType.slug] = gigType;
            return acc;
        }, {} as Record<string, GigType>) ?? {};
    }, [gigTypesArr])

   
    useEffect(() => {
        dispatch(fetchCampaignCreateDetails());
    }, [])

    useEffect(() => {
        if (campaignIdeas.length > 0 && (!selectedIdeaUUID || !campaignIdeas.find((i) => i.uuid === selectedIdeaUUID))) {
            setSelectedIdeaUUID(campaignIdeas[0].uuid);
        }
    }, [campaignIdeas, selectedIdeaUUID]);

    const selected = useMemo(() => {
        return campaignIdeas.find((i) => i.uuid === selectedIdeaUUID) ?? campaignIdeas[0] ?? null;
    }, [selectedIdeaUUID, campaignIdeas]);

    useEffect(() => {
        if (selected) {
            onIdeaSelect(selected);
        }
    }, [selected]);



    const storylineSteps = useMemo(() => {
        const c = selected?.campaign;
        const sl = c?.story_line;
        return [
            { step: 1 as const, title: 'Hook', body: sl?.hook ?? null },
            { step: 2 as const, title: 'Value', body: sl?.value ?? null },
            { step: 3 as const, title: 'Bridge', body: sl?.bridge ?? null },
            { step: 4 as const, title: 'CTA', body: sl?.cta ?? c?.call_to_action ?? null },
        ];
    }, [selected]);

    const dosList = useMemo(() => selected?.campaign?.dos ?? [], [selected]);
    const dontsList = useMemo(() => selected?.campaign?.donts ?? [], [selected]);

    const audienceGenderLabel = useMemo(() => {
        const g = selected?.campaign?.target_audience_demography?.gender as keyof typeof genders | undefined;
        return g ? (genders[g] ?? g) : null;
    }, [selected]);

    const audienceAgeGroups = useMemo(() => {
        const groups = selected?.campaign?.target_audience_demography?.age_groups ?? [];
        return groups.length ? groups.join(', ') : null;
    }, [selected]);

    const creatorGenderLabel = useMemo(() => {
        const g = selected?.campaign?.creator_criteria?.gender as keyof typeof genders | undefined;
        return g ? (genders[g] ?? g) : null;
    }, [selected]);

    const creatorAgeRange = useMemo(() => {
        const { min_age, max_age } = selected?.campaign?.creator_criteria ?? {};
        if (min_age && max_age) return `${min_age}-${max_age}`;
        if (min_age || max_age) return String(min_age ?? max_age);
        return null;
    }, [selected]);

    const creatorTierLabel = useMemo(() => {
        const b = selected?.campaign?.creator_criteria?.bucket?.trim();
        if (!b) return null;
        return b.charAt(0).toUpperCase() + b.slice(1).toLowerCase();
    }, [selected]);

    const skeletonBase = useMemo(
        () => ({
            baseColor: '#E9ECEF',
            highlightColor: '#F8F9FA',
        }),
        []
    );

    const renderText = (
        value: string | null | undefined,
        {
            width = '100%',
            height = 14,
            empty = '-',
        }: { width?: number | string; height?: number; empty?: string } = {}
    ) => {
        const v = typeof value === 'string' ? value.trim() : '';
        if (v) return generating ? <TypewriterText text={value as string} enabled={true} charsPerSecond={70} /> : value;
        if (generating) return <Skeleton {...skeletonBase} width={width} height={height} />;
        return empty;
    };

    const renderNode = (
        value: React.ReactNode,
        {
            available,
            width = '100%',
            height = 14,
            empty = '-',
        }: { available: boolean; width?: number | string; height?: number; empty?: string }
    ) => {
        if (available) return value;
        if (generating) return <Skeleton {...skeletonBase} width={width} height={height} />;
        return empty;
    };

    if (loading && campaignIdeas.length === 0) return (
        <div className="campaign-ai-strategy d-flex flex-column align-items-center justify-content-center vh-50">
            <div className="loader mb-2"></div>
            <div className="mt-2 text-muted" style={{ fontFamily: 'Rubik, sans-serif' }}>
                Fetching ideas...
            </div>
        </div>
    );


    return (
        <div className="campaign-ai-creation__grid">
            <div className="campaign-ai-creation__left" role="listbox" aria-label="Campaign ideas">
                {campaignIdeas.map((idea) => {
                    const selected = idea.uuid === selectedIdeaUUID;
                    const goal = idea.campaign?.goal ?? '';
                    const category = idea.campaign?.category ?? '';
                    return (
                        <button
                            key={idea.uuid}
                            type="button"
                            className={`campaign-ai-creation-item${selected ? ' campaign-ai-creation-item--selected' : ''}`}
                            onClick={() => {
                                setSelectedIdeaUUID(idea.uuid ?? null);
                            }}
                            role="option"
                            aria-selected={selected}
                        >
                            <div className="campaign-ai-idea-item__metaRow">
                                {goal ? (
                                    <span className="campaign-ai-pill campaign-ai-pill--blue">{goalList[goal]?.label ?? goal}</span>
                                ) : generating ? (
                                    <Skeleton {...skeletonBase} width={90} height={20} borderRadius={999} />
                                ) : null}
                                {category ? (
                                    <span className="campaign-ai-pill">{gigTypes[category]?.name ?? category}</span>
                                ) : generating ? (
                                    <Skeleton {...skeletonBase} width={90} height={20} borderRadius={999} />
                                ) : null}
                            </div>
                            <div className="campaign-ai-creation-item__name">
                                {renderText(idea.campaign?.name, { width: '78%', height: 18, empty: '-' })}
                            </div>
                            <div className="campaign-ai-creation-item__subline">
                                {renderText(idea.campaign?.objective ?? null, { width: '92%', height: 14, empty: '-' })}
                            </div>
                            <div className="campaign-ai-creation-item__criteria">
                                <span>
                                    {renderNode(
                                        <>
                                            {genders[idea.campaign?.target_audience_demography?.gender as keyof typeof genders] ??
                                                '-'}{' '}
                                            · {idea.campaign?.target_audience_demography?.age_groups?.join(', ') ?? '-'}
                                        </>,
                                        {
                                            available:
                                                Boolean(idea.campaign?.target_audience_demography?.gender) ||
                                                Boolean(idea.campaign?.target_audience_demography?.age_groups?.length),
                                            width: '72%',
                                            height: 14,
                                            empty: '-',
                                        }
                                    )}
                                </span>
                                <span>{renderText(idea.campaign?.creator_criteria?.niche ?? null, { width: '48%', height: 14, empty: '-' })}</span>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="campaign-ai-creation__right" aria-label="Strategy details">
                {selected && (
                    <div className="campaign-ai-creation-detail">
                        <div>

                            <div className="campaign-ai-idea-item__metaRow">
                                {selected.campaign?.goal ? (
                                    <span className="campaign-ai-pill campaign-ai-pill--blue">
                                        {goalList[selected.campaign.goal]?.label ?? selected.campaign.goal}
                                    </span>
                                ) : generating ? (
                                    <Skeleton {...skeletonBase} width={96} height={20} borderRadius={999} />
                                ) : null}
                                {selected.campaign?.category ? (
                                    <span className="campaign-ai-pill">
                                        {gigTypes[selected.campaign.category]?.name ?? selected.campaign.category}
                                    </span>
                                ) : generating ? (
                                    <Skeleton {...skeletonBase} width={96} height={20} borderRadius={999} />
                                ) : null}
                            </div>
                            <div className="campaign-ai-creation-detail__title">
                                {renderText(selected.campaign?.name, { width: '62%', height: 22, empty: '-' })}
                            </div>
                        </div>


                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">CREATORS BRIEF</div>
                            <div className="campaign-ai-field">
                                <div className="d-flex gap-4 flex-column">
                                    <span>
                                        {selected?.campaign?.brief?.trim()
                                                ? generating
                                                    ? <TypewriterText text={selected.campaign.brief} enabled={true} charsPerSecond={70} />
                                                    : selected.campaign.brief
                                            : generating
                                                ? (
                                                    <>
                                                        <Skeleton {...skeletonBase} height={14} width="95%" />
                                                        <Skeleton {...skeletonBase} height={14} width="88%" style={{ marginTop: 10 }} />
                                                        <Skeleton {...skeletonBase} height={14} width="74%" style={{ marginTop: 10 }} />
                                                    </>
                                                )
                                                : '-'}
                                    </span>
                                    <div className="d-flex gap-3" aria-label="Criteria">
                                        <div className="campaign-ai-subcard justify-content-start w-50">
                                            <div className="campaign-ai-subcard__k">CALL TO ACTION</div>
                                            <div className="campaign-ai-subcard__v">
                                                {renderText(selected.campaign?.call_to_action ?? null, { width: '80%', height: 14, empty: '-' })}
                                            </div>
                                        </div>
                                        <div className="campaign-ai-subcard justify-content-start w-50">
                                            <div className="campaign-ai-subcard__k">HASHTAGS</div>
                                            <div className="campaign-ai-subcard__v">
                                                <div className="d-flex gap-1 flex-wrap">
                                                    {(selected.campaign?.hashtag_suggestions ?? []).length > 0 ? (
                                                        (selected.campaign?.hashtag_suggestions ?? []).slice(0, 6).map((t) => (
                                                            <span key={t} className="campaign-ai-idea-tag">{t}</span>
                                                        ))
                                                    ) : generating ? (
                                                        Array.from({ length: 4 }).map((_, idx) => (
                                                            <Skeleton key={idx} {...skeletonBase} width={78} height={22} borderRadius={999} />
                                                        ))
                                                    ) : (
                                                        <span>-</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>

                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">STORYLINE</div>
                            <div className="campaign-ai-field campaign-ai-field--storyline">
                                <div className="campaign-ai-storyline-stepper" role="list" aria-label="Campaign storyline">
                                    {storylineSteps.map(({ step, title, body }, idx) => (
                                        <div key={step} className="campaign-ai-storyline-stepper__col" role="listitem">
                                            <div className="campaign-ai-storyline-stepper__rail" aria-hidden={true}>
                                                {idx === 0 ? (
                                                    <span className="campaign-ai-storyline-stepper__railSpacer" />
                                                ) : (
                                                    <span
                                                        className={`campaign-ai-storyline-stepper__connector campaign-ai-storyline-stepper__connector--head${idx === 1 ? '12' : idx === 2 ? '23' : '34'}`}
                                                    />
                                                )}
                                                <span className={`campaign-ai-storyline-stepper__bubble campaign-ai-storyline-stepper__bubble--${step}`}>{step}</span>
                                                {idx === 3 ? (
                                                    <span className="campaign-ai-storyline-stepper__railSpacer" />
                                                ) : (
                                                    <span
                                                        className={`campaign-ai-storyline-stepper__connector campaign-ai-storyline-stepper__connector--tail${idx === 0 ? '12' : idx === 1 ? '23' : '34'}`}
                                                    />
                                                )}
                                            </div>
                                            <div className="campaign-ai-storyline-stepper__colTitle">{title}</div>
                                            <p className="campaign-ai-storyline-stepper__colBody">
                                                {renderText(body, { width: '92%', height: 14, empty: '-' })}
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">DO'S & DON'TS</div>
                            <div className="campaign-ai-field campaign-ai-field--dosdonts">
                                <div className="campaign-ai-dosdonts">
                                    <div className="campaign-ai-dosdonts__col">
                                        <h3 className="campaign-ai-dosdonts__heading campaign-ai-dosdonts__heading--do">DO&apos;S</h3>
                                        <ul className="campaign-ai-dosdonts__list">
                                            {dosList.length > 0 ? (
                                                dosList.map((line, idx) => (
                                                    <li key={`do-${idx}`} className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--do">
                                                        {generating ? (
                                                            <TypewriterText text={line} enabled={true} charsPerSecond={90} showCaret={false} />
                                                        ) : (
                                                            line
                                                        )}
                                                    </li>
                                                ))
                                            ) : generating ? (
                                                Array.from({ length: 4 }).map((_, idx) => (
                                                    <li key={`do-skel-${idx}`} className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--do">
                                                        <Skeleton {...skeletonBase} height={14} width="92%" />
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--do campaign-ai-dosdonts__item--empty">—</li>
                                            )}
                                        </ul>
                                    </div>
                                    <div className="campaign-ai-dosdonts__col">
                                        <h3 className="campaign-ai-dosdonts__heading campaign-ai-dosdonts__heading--dont">DON&apos;TS</h3>
                                        <ul className="campaign-ai-dosdonts__list">
                                            {dontsList.length > 0 ? (
                                                dontsList.map((line, idx) => (
                                                    <li key={`dont-${idx}`} className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--dont">
                                                        {generating ? (
                                                            <TypewriterText text={line} enabled={true} charsPerSecond={90} showCaret={false} />
                                                        ) : (
                                                            line
                                                        )}
                                                    </li>
                                                ))
                                            ) : generating ? (
                                                Array.from({ length: 4 }).map((_, idx) => (
                                                    <li key={`dont-skel-${idx}`} className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--dont">
                                                        <Skeleton {...skeletonBase} height={14} width="92%" />
                                                    </li>
                                                ))
                                            ) : (
                                                <li className="campaign-ai-dosdonts__item campaign-ai-dosdonts__item--dont campaign-ai-dosdonts__item--empty">—</li>
                                            )}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="campaign-ai-section">
                            <div className="campaign-ai-section__label">TARGETING</div>
                            <div className="campaign-ai-field campaign-ai-field--targeting">
                                <div className="campaign-ai-targeting">
                                    <div className="campaign-ai-targeting__col">
                                        <h3 className="campaign-ai-targeting__subheading">Audience Targeting</h3>
                                        <div className="campaign-ai-targeting__stack">
                                            <div className="campaign-ai-targeting__card">
                                                <div className="campaign-ai-targeting__row campaign-ai-targeting__row--audience">
                                                    <span className="campaign-ai-targeting__k">Gender</span>
                                                    <span className="campaign-ai-targeting__v campaign-ai-targeting__v--audience">
                                                        {renderText(audienceGenderLabel, { width: 110, height: 14, empty: '-' })}
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="campaign-ai-targeting__card">
                                                <div className="campaign-ai-targeting__row campaign-ai-targeting__row--audience">
                                                    <span className="campaign-ai-targeting__k">Age Group</span>
                                                    <span className="campaign-ai-targeting__v campaign-ai-targeting__v--audience">
                                                        {renderText(audienceAgeGroups, { width: 140, height: 14, empty: '-' })}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="campaign-ai-targeting__col">
                                        <h3 className="campaign-ai-targeting__subheading">Creator Criteria</h3>
                                        <div className="campaign-ai-targeting__card campaign-ai-targeting__card--creator">
                                            {(
                                                [
                                                    { k: 'Niche', v: selected.campaign?.creator_criteria?.niche ?? '—' },
                                                    { k: 'Tier', v: creatorTierLabel },
                                                    { k: 'Gender', v: creatorGenderLabel },
                                                    { k: 'Age', v: creatorAgeRange },
                                                    { k: 'Style', v: selected.campaign?.creator_criteria?.content_style ?? '—' },
                                                ] as const
                                            ).map((row, idx, arr) => (
                                                <div
                                                    key={row.k}
                                                    className={`campaign-ai-targeting__row campaign-ai-targeting__row--creator${idx < arr.length - 1 ? ' campaign-ai-targeting__row--creatorSep' : ''}`}
                                                >
                                                    <span className="campaign-ai-targeting__k">{row.k}</span>
                                                    <span className="campaign-ai-targeting__v campaign-ai-targeting__v--creator">
                                                        {renderText(typeof row.v === 'string' ? row.v : row.v ?? null, { width: 160, height: 14, empty: '-' })}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default AICampaignIdeas